import { MouseEvent, ReactNode, forwardRef, useImperativeHandle } from 'react';
import { useDropzone } from 'react-dropzone';

import ErrorMessageStyled from 'approot/shared/error-message/error-message.style';
import IconUpload from 'approot/shared/icons/icon-upload';
import { useFileDropperContext } from 'lib/form/file-dropper/file-dropper.context';
import { useFileDropperHandlers } from 'lib/form/file-dropper/file-dropper.hooks';
import {
  getAcceptExtensions,
  getOptionalProps,
} from 'lib/form/file-dropper/file-dropper.utils';
import { FileDropperProgress } from 'lib/form/file-dropper/file-dropper';
import * as FileDropperStyled from 'lib/form/file-dropper/file-dropper.style';
import * as FileDropperImageStyled from 'lib/form/file-dropper/file-dropper-image.style';
import { AcceptTypes } from 'lib/form/file-dropper/file-dropper.constants';

const Styled = {
  ...FileDropperStyled,
  ...FileDropperImageStyled,
};

type Props = {
  placeholder?: ReactNode;
  accept?: AcceptTypes;
  square?: boolean;
  inputDataSelector?: string;
  name: string;
  error?: string;
  disabled?: boolean;
  validationError?: string;
  isLoading?: boolean;
  isFinishing?: boolean;
  additionalLabel?: string;
  onFileRemoved?: () => void;
  onChange: (props: { key: string; fileName: string; file: File }) => void;
};

export const FileDropperRive = forwardRef<{ click: () => void }, Props>(
  (
    {
      square,
      inputDataSelector,
      onChange,
      name,
      accept,
      error,
      validationError,
      isLoading,
      isFinishing,
      disabled,
      additionalLabel,
      onFileRemoved,
    }: Props,
    ref
  ) => {
    const { file, error: uploadError } = useFileDropperContext(name);

    useImperativeHandle(ref, () => ({
      click: () => {
        inputRef.current?.click();
      },
    }));

    const { onDrop, inputRef, launchFileDialog, inputId, progress } =
      useFileDropperHandlers({
        name,
        onChange: (key, fileName, file) => onChange({ key, fileName, file }),
      });

    const optionalProps = getOptionalProps(accept);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      multiple: false,
      disabled,
      ...optionalProps,
    });

    const hasFileError = error || uploadError;
    const displayError = hasFileError || validationError;

    return (
      <>
        {file ? (
          <FileDropperProgress
            file={file}
            name={name}
            progress={progress}
            isFinishing={isFinishing}
            error={uploadError}
            onClearFile={onFileRemoved}
          />
        ) : (
          <>
            <Styled.FileDropperImageField
              {...getRootProps({
                onClick: (e: MouseEvent) => {
                  e.stopPropagation();
                  launchFileDialog();
                },
              })}
              {...optionalProps}
              $activeDrag={isDragActive}
              $isLoading={isLoading}
              $hasError={!!(displayError || validationError)}
            >
              <Styled.FileDropperInput
                id={inputId}
                name={name}
                data-selector={inputDataSelector}
                {...getInputProps()}
                disabled={disabled}
                aria-label="Select a file to upload"
                ref={inputRef}
                onClick={() => {
                  if (hasFileError && inputRef && inputRef.current) {
                    inputRef.current.value = '';
                  }
                }}
                aria-describedby={`file-error-${name}`}
                aria-invalid={!!displayError}
              />
              <Styled.FileDropperImagePlaceholder>
                <IconUpload width={24} height={24} />

                <Styled.FileDropperText>
                  Drag and drop your .RIV file here
                </Styled.FileDropperText>
                {additionalLabel ? (
                  <Styled.FileDropperSecondaryText>
                    {additionalLabel}
                  </Styled.FileDropperSecondaryText>
                ) : (
                  <>
                    {accept && (
                      <Styled.FileDropperSecondaryText>
                        Files supported: {getAcceptExtensions(accept)}
                      </Styled.FileDropperSecondaryText>
                    )}
                  </>
                )}
              </Styled.FileDropperImagePlaceholder>

              {(displayError || validationError) && (
                <ErrorMessageStyled
                  id={`file-error-${name}`}
                  data-selector={`file-error-${name}`}
                >
                  {displayError}
                </ErrorMessageStyled>
              )}
            </Styled.FileDropperImageField>
          </>
        )}
      </>
    );
  }
);
